import { useEffect, useMemo, useRef, useState } from 'react';
import {
  addDays, addMonths, endOfMonth, monthGrid, startOfMonth, startOfWeek, weekdayIndex,
  type IsoDate,
} from '@hestia/core';
import { Icon, icons } from '../../ui.js';
import { longDate, monthTitle, relativeDay, weekday } from '../../lang.js';

/** Choosing a day.
 *
 *  The chips are always there and settle most tasks in one tap; the month opens
 *  underneath them for the rest. They are one control, not two levels — a chip
 *  that opens a panel that opens a calendar is a level too deep, and people lose
 *  track of where they are.
 *
 *  Hand-built rather than `<input type="date">`, which this used to be. On iOS
 *  the native control cannot be opened from script, ignores `min`/`max`, cannot
 *  be styled to match anything near it, paints ghost text over an empty value,
 *  and has a documented history of not opening at all inside a home-screen web
 *  app — which is exactly where this runs. It also rendered as `09/07/2026`,
 *  which is two different days depending on where you live, and needed a line of
 *  prose underneath to say which one it meant. */

const WD = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

/** Today, tomorrow, and the next weekend day. Three, plus the calendar button —
 *  a chip row that wraps onto a second line is a menu wearing a costume. */
export function quickDates(today: IsoDate): Array<[string, IsoDate]> {
  const out: Array<[string, IsoDate]> = [
    ['Today', today],
    ['Tomorrow', addDays(today, 1)],
  ];
  const saturday = addDays(startOfWeek(today), 5);
  const weekend = saturday > addDays(today, 1) ? saturday : addDays(saturday, 7);
  out.push([weekday(weekend, false), weekend]);
  return out;
}

export function DateChips({ value, end, today, open, onPick, onToggle }: {
  value: IsoDate;
  end: IsoDate | null;
  today: IsoDate;
  open: boolean;
  onPick: (d: IsoDate) => void;
  onToggle: () => void;
}) {
  const quick = useMemo(() => quickDates(today), [today]);
  // A span is never one of the quick days — it is a stretch, so it always shows
  // itself in full on the button that opens the month.
  const custom = end !== null || !quick.some(([, d]) => d === value);
  return (
    <div className="chiprow">
      {quick.map(([label, d]) => (
        <button key={label} type="button" className={`qchip${!end && value === d ? ' on' : ''}`}
                aria-pressed={!end && value === d} onClick={() => onPick(d)}>{label}</button>
      ))}
      <button type="button" className={`qchip${custom ? ' on' : ''}${open ? ' open' : ''}`}
              aria-expanded={open} onClick={onToggle}
              aria-label={end ? `${longDate(value)} to ${longDate(end)}. Change it`
                : custom ? `${longDate(value)}. Choose another date` : 'Choose a date'}>
        <Icon path={end ? icons.span : icons.calendar} size={16} />
        {custom && (
          <span style={{ marginLeft: 7 }}>
            {end ? `${short(value)} – ${short(end)}` : relativeDay(value, today)}
          </span>
        )}
      </button>
    </div>
  );
}

/** "12 Sep" — short enough to sit on a chip beside another one. */
const short = (d: IsoDate) => new Date(`${d}T00:00:00Z`)
  .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

export function MonthGrid({ value, end, today, onPick, onPickEnd }: {
  value: IsoDate;
  end: IsoDate | null;
  today: IsoDate;
  onPick: (d: IsoDate) => void;
  /** Null clears the span back to a single day. */
  onPickEnd: (d: IsoDate | null) => void;
}) {
  /** Choosing the last day is a mode you enter deliberately and leave as soon
   *  as it is done — a calendar where any tap might mean "start" or "end"
   *  depending on hidden state is a calendar nobody trusts. */
  const [pickingEnd, setPickingEnd] = useState(false);
  const [month, setMonth] = useState<IsoDate>(startOfMonth(value));
  useEffect(() => { setMonth(startOfMonth(value)); }, [value]);

  const grid = useMemo(() => monthGrid(month), [month]);
  const prefix = month.slice(0, 7);

  // Roving focus: one tab stop for the whole grid, arrows move inside it. A stop
  // per day would put 42 of them between the calendar and the button below it.
  const [focused, setFocused] = useState<IsoDate>(value);
  const gridRef = useRef<HTMLDivElement>(null);
  useEffect(() => { setFocused(value); }, [value]);

  /** Moving the focused day is a state change, so the cell to focus may not be on
   *  screen yet — and when the move crosses a month boundary it certainly is not.
   *  Chasing it in a `requestAnimationFrame` looks right and is not: the frame can
   *  arrive before React has committed, and the focus lands on the cell that was
   *  there before. Do it after the render that made the cell instead. */
  const moving = useRef(false);
  useEffect(() => {
    if (!moving.current) return;
    moving.current = false;
    gridRef.current?.querySelector<HTMLButtonElement>(`[data-d="${focused}"]`)?.focus();
  }, [focused, month]);

  function onKey(e: React.KeyboardEvent) {
    const step: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    let next: IsoDate | null = null;
    if (e.key in step) next = addDays(focused, step[e.key]!);
    else if (e.key === 'Home') next = addDays(focused, -weekdayIndex(focused));
    else if (e.key === 'End') next = addDays(focused, 6 - weekdayIndex(focused));
    else if (e.key === 'PageUp') next = addMonths(focused, e.shiftKey ? -12 : -1);
    else if (e.key === 'PageDown') next = addMonths(focused, e.shiftKey ? 12 : 1);
    else return;
    e.preventDefault();
    moving.current = true;
    setFocused(next);
    setMonth(startOfMonth(next));
  }

  return (
    <div className="dp">
      <div className="dp-head">
        <button type="button" className="dp-nav" aria-label="Previous month"
                onClick={() => setMonth(startOfMonth(addDays(month, -1)))}>
          <Icon path={icons.chevronLeft} size={17} />
        </button>
        {/* The live region has to exist before its text changes, or a month
            step is silently swallowed — so it is always rendered, never
            conditionally. Announcing the grid itself would read out 42 cells. */}
        <div className="dp-title" aria-live="polite">{monthTitle(month)}</div>
        <button type="button" className="dp-nav" aria-label="Next month"
                onClick={() => setMonth(addDays(endOfMonth(month), 1))}>
          <Icon path={icons.chevronRight} size={17} />
        </button>
      </div>

      <div className="dp-wd" aria-hidden>{WD.map((w, i) => <span key={i}>{w}</span>)}</div>

      <div className="dp-grid" role="grid" ref={gridRef} onKeyDown={onKey} aria-label="Choose a date">
        {grid.map((d) => {
          const inSpan = end !== null && d > value && d < end;
          const edge = d === value || d === end;
          return (
            <button key={d} data-d={d} role="gridcell" type="button"
                    className={`dp-day${d.startsWith(prefix) ? '' : ' out'}`
                               + `${edge ? ' on' : ''}${inSpan ? ' between' : ''}`
                               + `${d === today ? ' now' : ''}`
                               + `${end !== null && d === value ? ' from' : ''}`
                               + `${end !== null && d === end ? ' to' : ''}`}
                    tabIndex={d === focused ? 0 : -1} aria-selected={edge}
                    aria-current={d === today ? 'date' : undefined}
                    // The visible label is a bare number, which tells a screen
                    // reader nothing about which day it is.
                    aria-label={longDate(d)}
                    onFocus={() => setFocused(d)}
                    onClick={() => {
                      if (!pickingEnd) { onPickEnd(null); onPick(d); return; }
                      setPickingEnd(false);
                      // Tapping backwards means they meant a new start.
                      if (d <= value) { onPickEnd(null); onPick(d); return; }
                      onPickEnd(d);
                    }}>
              {Number(d.slice(8))}
            </button>
          );
        })}
      </div>

      {/* The grid says "14"; this says which 14, and announces a pick to anyone
          who is not looking at the highlight. */}
      <div className="dp-foot">
        <div className="dp-echo" aria-live="polite">
          {end ? `${longDate(value)} → ${longDate(end)}` : longDate(value)}
        </div>
        {end ? (
          <button type="button" className="panel-clear" onClick={() => { setPickingEnd(false); onPickEnd(null); }}>
            Just one day
          </button>
        ) : (
          <button type="button" className={`panel-clear${pickingEnd ? ' armed' : ''}`}
                  aria-pressed={pickingEnd} onClick={() => setPickingEnd((v) => !v)}>
            {pickingEnd ? 'Pick the last day' : 'Runs for several days'}
          </button>
        )}
      </div>
    </div>
  );
}
